"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
const rxjs_1 = require("rxjs");
/**
 * Manages MQTT protocol over a single websocket.
 *
 * @class MqttSocket
 */
class MqttSocket {
    /**
     * Creates an instance of MqttSocket
     * @constructor
     * @param {string} params.clientId mqtt client id
     * @param {Function} params.clientFactory factory method for creating paho client instances
     */
    constructor(params) {
        this.pahoClientInstance = 0;
        this.clientId = params.clientId;
        this.clientFactory = params.clientFactory;
        this.connectivitySubject = new rxjs_1.Subject();
        this.messageArrivedSubject = new rxjs_1.Subject();
        this.websocketSubject = new rxjs_1.Subject();
    }
    /**
     * Gets the client id for this instance
     */
    getClientId() {
        return this.clientId;
    }
    /**
     * Creates a socket and begins the MQTT connection process.  The returned promise will resolve when the connection is established.
     *
     * @param {string} params.url websocket URL from the token login response
     */
    connect(params) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.connectionPromise) {
                throw new Error(`Cannot connect when a connection is in progress.`);
            }
            else {
                return this.connectionPromise = this.startConnection(params);
            }
        });
    }
    startConnection(params) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.isConnected()) {
                yield this.disconnect();
            }
            try {
                const pahoConnect = this.pahoConnect(params.url);
                yield pahoConnect;
                this.isPahoConnected = true;
                return pahoConnect;
            }
            finally {
                this.connectionPromise = undefined;
            }
        });
    }
    pahoConnect(websocketUrl) {
        return new Promise((resolve, reject) => {
            this.pahoClient = this.clientFactory(websocketUrl, this.clientId);
            const context = { instanceId: ++this.pahoClientInstance };
            this.pahoClient.context = context;
            this.listenToEvents(this.pahoClient);
            Promise.resolve().then(() => {
                this.connectivitySubject.next({
                    type: 'connecting',
                    data: { invocationContext: context }
                });
            });
            this.pahoClient.connect({
                useSSL: true,
                timeout: 5,
                keepAliveInterval: 60,
                mqttVersion: 4,
                onSuccess: response => {
                    this.connectivitySubject.next({
                        type: 'connected',
                        data: { invocationContext: context }
                    });
                    resolve(response);
                },
                onFailure: response => {
                    response.invocationContext = context;
                    this.connectivitySubject.next({
                        type: 'error',
                        data: response
                    });
                    reject(this.createError(response.errorCode, response.errorMessage));
                },
            });
        });
    }
    /**
     * Indicates whether the socket is connected.
     */
    isConnected() {
        return this.pahoClient !== undefined && this.isPahoConnected;
    }
    /**
     * Indicates whether the socket is in the middle of establishing a connection.
     */
    isConnecting() {
        return !!this.connectionPromise;
    }
    /**
     * Subscribes to websocket events for a subscription.
     *
     * @param {string} params.subscriptionId ID of the subscription from which to receive publications over the websocket.
     */
    subscribe(params) {
        if (!this.isConnected()) {
            return Promise.reject(new Error('not connected'));
        }
        else {
            return new Promise((resolve, reject) => {
                this.pahoClient.subscribe(`${this.getPahoClientId()}/${params.subscriptionId}`, {
                    onSuccess: response => resolve(response),
                    onFailure: response => reject(this.createError(response.errorCode, response.errorMessage)),
                });
            });
        }
    }
    /**
     * Unsubscribe from websocket events for a subscription.
     *
     * @param {string} params.subscriptionId ID of the subscription from which to stop receiving publications over the websocket.
     */
    unsubscribe(params) {
        if (!this.isConnected()) {
            return Promise.reject(new Error('not connected'));
        }
        else {
            return new Promise((resolve, reject) => {
                this.pahoClient.unsubscribe(`${this.getPahoClientId()}/${params.subscriptionId}`, {
                    onSuccess: response => resolve(response),
                    onFailure: response => reject(this.createError(response.errorCode, response.errorMessage)),
                });
            });
        }
    }
    getPahoClientId() {
        return this.pahoClient.clientId;
    }
    /**
     * Disconnects the websocket.
     */
    disconnect(_params = {}) {
        return new Promise((resolve, _reject) => {
            if (this.isConnected()) {
                this.connectivitySubject
                    .filter(event => event.type === 'disconnected')
                    .subscribe(_response => resolve());
                this.pahoClient.disconnect();
            }
            else {
                resolve();
            }
        });
    }
    listenToEvents(pahoClient) {
        pahoClient.onConnectionLost = response => {
            response.invocationContext = pahoClient.context;
            this.isPahoConnected = false;
            this.connectivitySubject.next({
                type: 'disconnected',
                data: response,
            });
        };
        pahoClient.onMessageArrived = message => this.messageArrivedSubject.next(message);
        pahoClient.trace = event => this.websocketSubject.next(event.message);
    }
    /**
     * Observable for listening to received messages.
     */
    asMessageObservable() {
        return this.messageArrivedSubject;
    }
    /**
     * Observable for listening to connectivity messages.
     */
    asConnectivityObservable() {
        return this.connectivitySubject;
    }
    /**
     * Observable for listening to websocket trace messages.
     */
    asWebsocketObservable() {
        return this.websocketSubject;
    }
    createError(errorCode, errorMessage) {
        return new Error(`errorCode:[${errorCode}]; errorMessage:[${errorMessage}]`);
    }
}
exports.MqttSocket = MqttSocket;
